/**
 * AK-584 — состояние первичной настройки тенанта.
 *
 * Один источник правды для трёх мест, которые раньше не знали друг о друге:
 *   - мастер первого входа (`/onboarding/first-run`) — с какого шага начинать;
 *   - баннер «Настройка не закончена» в шапке;
 *   - чек-лист «продолжить настройку» (`/getting-started`).
 *
 * Считается по `/api/v1/dashboard/summary` — это состояние ТЕНАНТА, а не
 * «первый вход этого человека». Второму агроному того же клиента, который
 * заходит в уже заполненное хозяйство, мастер не нужен и баннер не положен
 * («Горизонт +1» из wireframe).
 */

export interface SetupSummary {
  /** Сколько хозяйств заведено. */
  farms: number;
  /** Сколько полей заведено — главный признак «система ожила». */
  fields: number;
  /** Есть ли запись компании (без неё хозяйство не создать). */
  has_company: boolean;
}

/** Строка из `/api/v1/scope/available` — ровно те поля, что нам нужны. */
export interface ScopeRow {
  scope_type: 'holding' | 'company' | 'cluster' | 'farm' | 'brigade';
  fields_count?: number | null;
  farms_count?: number | null;
  total_area_ha?: number | null;
}

export interface TenantCounts {
  fields: number;
  farms: number;
  totalAreaHa: number;
}

/**
 * Счётчики по ТЕНАНТУ из списка доступных скоупов.
 *
 * Почему не `/dashboard/summary`: та ручка фильтрует счётчики по активному
 * скоупу сессии (`build_scope_filter`), то есть «полей 0» там значит «в
 * выбранном хозяйстве нет полей», а не «в системе нет полей». Клиент с 800
 * полями, вставший скоупом на пустое хозяйство, получал бы «Настройка не
 * закончена» на каждой странице — и штатный путь фичи («＋ Поле» из дерева →
 * `/map?addField=1&farm=<id>`) сам взводил бы этот скоуп.
 *
 * `/scope/available` отдаёт строки, не зависящие от текущего выбора. Строки
 * пересекаются (холдинг ⊃ компания ⊃ кластер ⊃ хозяйство ⊃ отделение),
 * поэтому берём самый широкий доступный уровень, а при его отсутствии (роль
 * без обзора всей компании) складываем ХОЗЯЙСТВА — они делят поля без
 * пересечений, в отличие от кластеров и отделений.
 *
 * `null` — состав неизвестен (ручка упала / пустой ответ): тогда никаких
 * утверждений о настройке не делаем.
 */
export function tenantCountsFromScopes(
  rows: readonly ScopeRow[] | null | undefined,
): TenantCounts | null {
  if (!rows || rows.length === 0) return null;

  // Аварийный ответ ручки — одна строка-заглушка («Загрузка данных…») с
  // `fields_count: null` и HTTP 200. Через `?? 0` она читалась бы как «полей
  // нет», и на плохеющем бэкенде мы бы врали баннером живому клиенту.
  // Строка без ЧИСЛА полей — не ноль, а «неизвестно».
  const counted = rows.filter((r) => typeof r.fields_count === 'number');
  if (counted.length === 0) return null;

  const of = (type: ScopeRow['scope_type']): ScopeRow[] =>
    counted.filter((r) => r.scope_type === type);

  const holdings = of('holding');
  const companies = of('company');
  const farmRows = of('farm');

  // Поля и площадь — с самого широкого доступного уровня (строки вложены друг
  // в друга, суммировать разные уровни нельзя).
  const source =
    holdings.length > 0
      ? holdings
      : companies.length > 0
        ? companies
        : farmRows.length > 0
          ? farmRows
          : null;
  if (!source) return null;

  const fields = source.reduce((a, r) => a + (r.fields_count ?? 0), 0);
  const totalAreaHa = source.reduce((a, r) => a + (r.total_area_ha ?? 0), 0);

  // Хозяйства считаем ОТДЕЛЬНО и никогда не берём из holding-строки: там в
  // `farms_count` лежит число КОМПАНИЙ (`COUNT(*) FROM companies … AS
  // farms_count` в scope.py). Иначе на свежем тенанте, где дефолтный холдинг
  // есть у всех, чек-лист писал бы «Хозяйств: 1», пока мастер в ту же секунду
  // предлагает завести первое хозяйство.
  const farms =
    farmRows.length > 0
      ? farmRows.length
      : companies.reduce((a, r) => a + (r.farms_count ?? 0), 0);

  return { fields, farms, totalAreaHa };
}

/**
 * Шаг мастера:
 *   company — нет даже компании, хозяйство создавать не из чего;
 *   farm    — компания есть, хозяйств нет → «Начнём с хозяйства»;
 *   field   — хозяйство есть → сразу к полю (Р2: шаг хозяйства пропускает себя);
 *   done    — поле уже есть, мастеру нечего делать.
 */
export type SetupStep = 'company' | 'farm' | 'field' | 'done';

export function setupStep(summary: SetupSummary | null | undefined): SetupStep {
  // Пока сводка не пришла — считаем, что всё в порядке: лучше не показать
  // мастер лишний раз, чем мигнуть им человеку с 200 полями.
  if (!summary) return 'done';
  if (summary.fields > 0) return 'done';
  if (summary.farms > 0) return 'field';
  if (!summary.has_company) return 'company';
  return 'farm';
}

/**
 * Настройка не закончена ⇔ в тенанте нет ни одного поля. Именно поле, а не
 * хозяйство: хозяйство без полей ничего не показывает ни на карте, ни в
 * сводке, и человек честно упирается в пустоту.
 */
export function isSetupIncomplete(
  summary: { fields: number } | null | undefined,
): boolean {
  if (!summary) return false;
  return summary.fields === 0;
}

/**
 * Нужно ли на шаге «поле» спрашивать, в какое хозяйство рисовать.
 * Одно хозяйство — вопрос лишний, показываем фактом (Р2).
 */
export function shouldPickFarm(farmCount: number): boolean {
  return farmCount > 1;
}

/** Пункт чек-листа «продолжить настройку» (`/getting-started`). */
export interface SetupChecklistItem {
  key: 'farms' | 'fields' | 'requisites';
  title: string;
  description: string;
  done: boolean;
  /** Куда ведёт основное действие пункта. */
  path: string;
}

export interface ChecklistInput {
  farms: number;
  fields: number;
  total_area_ha: number;
}

/**
 * Чек-лист = те же шаги, что и мастер, но в виде списка с галочками.
 * Порядок фиксированный: хозяйство → поля → реквизиты (реквизиты всегда
 * «можно позже», галочку им не ставим — это не блокирующий шаг).
 */
export function setupChecklist(s: ChecklistInput): SetupChecklistItem[] {
  return [
    {
      key: 'farms',
      title: 'Хозяйство',
      // Поле не висит в воздухе: если поля есть, хозяйство точно заведено,
      // даже когда роль не видит строк хозяйств и число нам недоступно.
      description:
        s.farms > 0
          ? `Хозяйств: ${s.farms}`
          : s.fields > 0
            ? 'Хозяйства заведены'
            : 'Ни одного хозяйства — с него начинается структура',
      done: s.farms > 0 || s.fields > 0,
      path: '/company/structure',
    },
    {
      key: 'fields',
      title: 'Поля',
      description:
        s.fields === 0
          ? 'Ни одного поля. Обведите на карте или загрузите файл'
          : `Полей: ${s.fields.toLocaleString('ru-RU')} · ${Math.round(
              s.total_area_ha,
            ).toLocaleString('ru-RU')} га`,
      done: s.fields > 0,
      path: '/fields',
    },
    {
      key: 'requisites',
      title: 'Реквизиты компании',
      description: 'Юрлицо, валюта, логотип — можно позже',
      done: false,
      path: '/company/profile',
    },
  ];
}
